import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { DateInput } from "@/components/ui/date-input";

const NM = <span className="text-slate-400 text-sm font-medium" title="Aucune donnée enregistrée pour cette métrique">Non mesuré</span>;
const fmt = (v) => (v === null || v === undefined ? NM : v.toLocaleString("fr-FR"));
const dt = (s) => (s ? new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const PERIODS = [["today", "Aujourd'hui"], ["7d", "7 jours"], ["30d", "30 jours"], ["custom", "Personnalisée"]];
const SOURCE_FR = { "respondia.fr": "respondia.fr", direct: "Lien direct", campaign: "Campagne (UTM)", other: "Autre site", inconnu: "Non mesuré" };

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-200" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-heading text-3xl font-extrabold mt-2">{fmt(value)}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function Funnel({ steps }) {
  const max = Math.max(1, ...steps.map((s) => s.value || 0));
  return (
    <div className="space-y-2" data-testid="analytics-funnel">
      {steps.map((s, i) => (
        <div key={s.step} className="flex items-center gap-3" data-testid={`funnel-step-${i}`}>
          <div className="w-40 text-sm text-slate-600 shrink-0">{s.step}</div>
          <div className="flex-1 h-8 bg-slate-100 rounded-lg overflow-hidden">
            <div className="h-full bg-blue-600 rounded-lg transition-[width] duration-500" style={{ width: s.measured ? `${Math.max(2, (100 * (s.value || 0)) / max)}%` : "0%" }} />
          </div>
          <div className="w-16 text-right font-semibold text-sm">{fmt(s.value)}</div>
          <div className="w-16 text-right text-xs text-slate-400">{s.rate_from_prev != null ? `${s.rate_from_prev} %` : i ? "—" : ""}</div>
        </div>
      ))}
    </div>
  );
}

export function AdminAnalytics() {
  const [period, setPeriod] = useState("30d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [internal, setInternal] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (period === "custom" && !from) return;
    api.get("/admin/analytics/overview", { params: { period, date_from: from || undefined, date_to: to || undefined, include_internal: internal } })
      .then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(e.response?.data?.detail || "Erreur de chargement"));
  }, [period, from, to, internal]);

  const k = data?.kpis;
  const showChart = data && period !== "today" && data.series.some((d) => (d.visitors || 0) + d.signups > 0);
  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {PERIODS.map(([k2, l]) => (
            <Button key={k2} size="sm" variant={period === k2 ? "default" : "ghost"} onClick={() => setPeriod(k2)} data-testid={`period-${k2}`} className="rounded-lg h-8">{l}</Button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <DateInput value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 w-40" data-testid="period-from" />
            <span className="text-slate-400">→</span>
            <DateInput value={to} onChange={(e) => setTo(e.target.value)} className="h-8 w-40" data-testid="period-to" />
          </div>
        )}
        <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={internal} onCheckedChange={setInternal} data-testid="toggle-internal" /> Inclure comptes test / démo / admin
          {data && !internal && <span className="text-xs text-slate-400">({data.excluded_companies} exclus)</span>}
        </label>
      </div>
      {err && <div className="text-sm text-red-600" data-testid="analytics-error">{err}</div>}
      {!data ? <div className="text-slate-400 text-sm">Chargement…</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Kpi label="Visiteurs" value={k.visitors.value} testid="kpi-visitors" sub={k.visitors.measured ? `Mesuré depuis le ${new Date(k.visitors.since).toLocaleDateString("fr-FR")}` : "Suivi actif dès la première visite"} />
            <Kpi label="Inscriptions" value={k.signups} testid="kpi-signups" sub={`${k.companies_created} compte(s) · ${k.establishments_created} établissement(s)`} />
            <Kpi label="Essais Stripe" value={k.trials_started} testid="kpi-trials" sub={`E ${k.trials_by_plan.essential} · P ${k.trials_by_plan.pro} · Pr ${k.trials_by_plan.premium}`} />
            <Kpi label="Abonnements actifs" value={k.active} testid="kpi-active" sub={`${k.trialing} en essai · ${k.cancel_scheduled} résiliation prog.`} />
            <Kpi label="Conversion" value={k.conversion_signup_to_active != null ? `${k.conversion_signup_to_active} %` : null} testid="kpi-conversion" sub="Inscrits de la période → abonnement actif" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi label="Annulés (période)" value={k.canceled_period} testid="kpi-canceled" sub={`${k.canceled_total} au total`} />
            <Kpi label="Paiements échoués" value={k.payment_failed} testid="kpi-failed" sub={`${k.past_due} abonnement(s) impayé(s)`} />
            <Kpi label="Établissements créés" value={k.establishments_created} testid="kpi-ests" />
            <Kpi label="Comptes créés" value={k.companies_created} testid="kpi-companies" />
          </div>

          <section className="bg-white rounded-2xl p-6 border border-slate-200">
            <h3 className="font-heading font-bold">Tunnel de conversion</h3>
            <p className="text-xs text-slate-400 mb-4">Cohorte des inscrits de la période. Taux calculés uniquement sur des données enregistrées.</p>
            <Funnel steps={data.funnel} />
          </section>

          {showChart && (
            <section className="bg-white rounded-2xl p-6 border border-slate-200">
              <h3 className="font-heading font-bold mb-4">Visites et inscriptions par jour</h3>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
                  <Line type="monotone" dataKey="visitors" name="Visiteurs" stroke="#2563eb" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="signups" name="Inscriptions" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </section>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            <section className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="features-usage">
              <h3 className="font-heading font-bold">Fonctionnalités les plus utilisées</h3>
              <p className="text-xs text-slate-400 mb-3">{data.features_measured_since ? `Mesuré depuis le ${new Date(data.features_measured_since).toLocaleDateString("fr-FR")}` : "Aucune utilisation enregistrée pour l'instant"}</p>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-400 text-left"><th className="py-1">Fonctionnalité</th><th className="text-right">Utilisateurs</th><th className="text-right">Utilisations</th></tr></thead>
                <tbody>
                  {data.features.map((f) => (
                    <tr key={f.key} className="border-t border-slate-100" data-testid={`feature-${f.key}`}>
                      <td className="py-1.5">{f.label}</td><td className="text-right font-medium">{f.unique_users}</td><td className="text-right text-slate-500">{f.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <div className="space-y-6">
              <section className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="recent-signups">
                <h3 className="font-heading font-bold mb-3">Dernières inscriptions</h3>
                {data.recent_users.length === 0 && <div className="text-sm text-slate-400">Aucune inscription.</div>}
                {data.recent_users.map((u) => (
                  <div key={u.id} className="flex justify-between text-sm py-1.5 border-t border-slate-100 first:border-0">
                    <span className="truncate">{u.email}</span><span className="text-slate-400 text-xs shrink-0 ml-2">{SOURCE_FR[u.source || "inconnu"]} · {dt(u.created_at)}</span>
                  </div>
                ))}
              </section>
              <section className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="recent-events">
                <h3 className="font-heading font-bold mb-3">Dernières activités</h3>
                {data.recent_events.length === 0 && <div className="text-sm text-slate-400">Aucune activité enregistrée.</div>}
                {data.recent_events.map((e, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-t border-slate-100 first:border-0">
                    <span>{e.label}</span><span className="text-slate-400 text-xs ml-2 truncate">{e.email || "—"} · {dt(e.ts)}</span>
                  </div>
                ))}
              </section>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <section className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="plan-distribution">
              <h3 className="font-heading font-bold mb-3">Répartition des abonnements Stripe en cours</h3>
              <div className="grid grid-cols-3 gap-3">
                {["essential", "pro", "premium"].map((p) => (
                  <div key={p} className="rounded-xl bg-slate-50 p-4 text-center" data-testid={`plan-${p}`}>
                    <div className="text-xs uppercase text-slate-500">{p}</div>
                    <div className="font-heading text-2xl font-extrabold">{data.plan_distribution[p]}</div>
                  </div>
                ))}
              </div>
            </section>
            <section className="bg-white rounded-2xl p-6 border border-slate-200" data-testid="acquisition">
              <h3 className="font-heading font-bold mb-3">Acquisition des inscrits (période)</h3>
              {Object.keys(data.acquisition).length === 0 && <div className="text-sm text-slate-400">Aucune inscription sur la période.</div>}
              {Object.entries(data.acquisition).map(([s, n]) => (
                <div key={s} className="flex justify-between text-sm py-1.5 border-t border-slate-100 first:border-0"><span>{SOURCE_FR[s] || s}</span><span className="font-medium">{n}</span></div>
              ))}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
